/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import { DateRangePicker } from 'rsuite';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAnalysisStore } from '../AssetAnalysisStore';
import { useShallow } from 'zustand/react/shallow';

const quarters = [
	{ label: 'Q1 (Jan-Mar)', value: 1 },
	{ label: 'Q2 (Apr-Jun)', value: 2 },
	{ label: 'Q3 (Jul-Sep)', value: 3 },
	{ label: 'Q4 (Oct-Dec)', value: 4 },
];

const monthNames = [
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sep',
	'Oct',
	'Nov',
	'Dec',
];

const formatDate = (date) => {
	if (!date) return '';

	const day = date.getDate();
	const month = monthNames[date.getMonth()];
	const year = date.getFullYear().toString().slice(2);

	let daySuffix = 'th';
	if (day % 10 === 1 && day !== 11) daySuffix = 'st';
	else if (day % 10 === 2 && day !== 12) daySuffix = 'nd';
	else if (day % 10 === 3 && day !== 13) daySuffix = 'rd';

	return `${day}${daySuffix} ${month}'${year}`;
};

const DateRangeSelector = () => {
	const {
		activeTimeRangeDropdown,
		activeConfigType,
		dateRangeObj,
		setDateRangeObj,
		activeAssetWiseDropdown,
		activeGeographyWiseDropdown,
		dateInfo,
		setDateInfo,
	} = useAnalysisStore(
		useShallow((state) => ({
			activeTimeRangeDropdown: state.activeTimeRangeDropdown,
			activeConfigType: state.activeConfigType,
			dateInfo: state.dateInfo,
			activeAssetWiseDropdown: state.activeAssetWiseDropdown,
			activeGeographyWiseDropdown: state.activeGeographyWiseDropdown,
			setDateInfo: state.setDateInfo,
			dateRangeObj: state.dateRangeObj,
			setDateRangeObj: state.setDateRangeObj,
		}))
	);

	const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
	const [currentQuarter, setCurrentQuarter] = useState(
		Math.floor(new Date().getMonth() / 3) + 1
	);
	const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
	const [isPickerOpen, setIsPickerOpen] = useState(false);
	const [yearChangeHighlight, setYearChangeHighlight] = useState(false);
	const pickerRef = useRef(null);
	const prevYearRef = useRef(currentYear);

	// Function to format date for display

	// Handle date range change for custom date picker (non-time view)
	const handleCustomDateChange = (dateRange) => {
		if (dateRange && dateRange.length === 2) {
			const [start, end] = dateRange;

			setDateRangeObj({
				from: start,
				to: end,
				viewFormat: {
					from: formatDate(start),
					to: formatDate(end),
				},
			});
		}
	};
	// Updates dateInfo for time-based views
	const updateDateSelection = (period, options = {}) => {
		// Create base dateInfo object
		const updatedDateInfo = {
			year: options.year || new Date().getFullYear(),
			period: period,
		};

		// Add period-specific details
		if (period === 'month' && options.month !== undefined) {
			updatedDateInfo.month = options.month; // Use provided month (1-based)
		}

		if (period === 'quarter' && options.quarter !== undefined) {
			updatedDateInfo.quarter = options.quarter;
		}

		// Update state
		setDateInfo(updatedDateInfo);
	};

	// Handle month selection for day view
	const handleMonthSelect = (date) => {
		if (!date) return;

		const year = date.getFullYear();
		const month = date.getMonth();
		
		setCurrentMonth(month);
		setCurrentYear(year);
		
		// Update with month (convert to 1-based for API)
		updateDateSelection('month', { 
			year: year,
			month: month + 1 
		});
		
		setIsPickerOpen(false);
	};

	// Quarter selection handler
	const handleQuarterSelect = (quarterIndex) => {
		setCurrentQuarter(quarterIndex);
		
		if (quarterIndex >= 1 && quarterIndex <= 4) {
			updateDateSelection('quarter', {
				year: currentYear,
				quarter: quarterIndex
			});
		}
		
		setIsPickerOpen(false);
	};

	// Year selection handler
	const handleYearSelect = (year) => {
		setCurrentYear(year);

		if (activeTimeRangeDropdown === 'quarter') {
			// For week wise view (labeled as quarter in the code)
			const validQuarter = Math.min(Math.max(currentQuarter, 1), 4);
			
			updateDateSelection('quarter', {
				year: year,
				quarter: validQuarter
			});
		} else if (activeTimeRangeDropdown === 'year') {
			// For month wise view (full year)
			updateDateSelection('year', { 
				year: year 
			});
		}

		// setIsPickerOpen(false);
	};

	// Navigate between date ranges (previous/next)
	const handleNavigate = (direction) => {
		if (!dateInfo.year) return;

		switch (activeTimeRangeDropdown) {
			case 'month':
				// Navigate by month
				let newMonth = dateInfo.month;
				let newYear = dateInfo.year;
				
				if (direction === 'prev') {
					if (newMonth === 1) {
						newMonth = 12;
						newYear--;
					} else {
						newMonth--;
					}
				} else {
					if (newMonth === 12) {
						newMonth = 1;
						newYear++;
					} else {
						newMonth++;
					}
				}
				
				updateDateSelection('month', {
					year: newYear,
					month: newMonth
				});
				
				setCurrentMonth(newMonth - 1);
				setCurrentYear(newYear);
				break;

			case 'quarter':
				// Navigate by quarter
				if (direction === 'prev') {
					if (currentQuarter > 1) {
						handleQuarterSelect(currentQuarter - 1);
					} else {
						// Go to Q4 of previous year
						setCurrentYear(currentYear - 1);
						handleQuarterSelect(4);
					}
				} else {
					if (currentQuarter < 4) {
						handleQuarterSelect(currentQuarter + 1);
					} else {
						// Go to Q1 of next year
						setCurrentYear(currentYear + 1);
						handleQuarterSelect(1);
					}
				}
				break;

			case 'year':
				// Navigate by year
				if (direction === 'prev') {
					handleYearSelect(currentYear - 1);
				} else {
					handleYearSelect(currentYear + 1);
				}
				break;

			default:
				break;
		}
	};

	// Set default date range when time range changes
	useEffect(() => {
		const now = new Date();
		const currentYear = now.getFullYear();
		const currentMonth = now.getMonth() + 1; // 1-based month
		const currentQuarterNum = Math.floor((now.getMonth()) / 3) + 1;
		
		setCurrentYear(currentYear);
		setCurrentQuarter(currentQuarterNum);
		setCurrentMonth(now.getMonth()); // 0-based for state

		switch (activeTimeRangeDropdown) {
			case 'month':
				// Default to current month
				updateDateSelection('month', {
					year: currentYear,
					month: currentMonth
				});
				break;

			case 'quarter':
				// Default to current quarter
				updateDateSelection('quarter', {
					year: currentYear,
					quarter: currentQuarterNum
				});
				break;

			case 'year':
				// Default to current year
				updateDateSelection('year', {
					year: currentYear
				});
				break;

			default:
				return;
		}
	}, [activeTimeRangeDropdown]);

	// Track year changes and show highlight
	useEffect(() => {
		if (prevYearRef.current !== currentYear) {
			setYearChangeHighlight(true);
			const timer = setTimeout(() => {
				setYearChangeHighlight(false);
			}, 1500);
			
			prevYearRef.current = currentYear;
			
			return () => clearTimeout(timer);
		}
	}, [currentYear]);

	useEffect(() => {
		if(!dateRangeObj.from || !dateRangeObj.to) {
			// Set date range to current month (first day to last day)
			const today = new Date();
			const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
			setDateRangeObj({
				from: firstDayOfMonth,
				to: today,
				viewFormat: {
					from: formatDate(firstDayOfMonth),
					to: formatDate(today)
				}
			});
		}
	}, [activeAssetWiseDropdown, activeGeographyWiseDropdown]);

	const getYearOptions = () => {
		const currentYear = new Date().getFullYear();
		return [currentYear, currentYear - 1, currentYear - 2, currentYear - 3];
	};

	// Render the date picker for time view
	const renderTimePicker = () => {
		if (!isPickerOpen) return null;

		if (activeTimeRangeDropdown === 'month') {
			// Day wise view shows a month selector
			return (
				<div
					className='absolute top-full left-0 mt-1 z-50 bg-white shadow-lg rounded-md p-2 border border-gray-200'
					ref={pickerRef}
				>
					<div className='mb-2'>
						<p className='text-xs text-gray-500 mb-1'>
							Select Month
						</p>
						<div className='grid grid-cols-3 gap-1'>
							{Array.from({ length: 12 }, (_, i) => {
								const isCurrentMonth = currentMonth === i;
								const isFutureDate =
									new Date(currentYear, i, 1) > new Date();

								return (
									<button
										key={i}
										disabled={isFutureDate}
										className={`text-xs py-1 px-2 rounded ${
											isCurrentMonth
												? 'bg-blue-100 text-blue-600'
												: isFutureDate
												? 'bg-gray-50 text-gray-300 cursor-not-allowed'
												: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
										}`}
										onClick={() => {
											const newDate = new Date(
												currentYear,
												i,
												1
											);
											handleMonthSelect(newDate);
										}}
									>
										{monthNames[i]}
									</button>
								);
							})}
						</div>
					</div>

					<div>
						<p className='text-xs text-gray-500 mb-1'>Year</p>
						<div className='grid grid-cols-2 gap-1'>
							{getYearOptions().map((year) => {
								const isCurrentYear = currentYear === year;
								const isFutureYear = year > new Date().getFullYear();

								return (
									<button
										key={year}
										disabled={isFutureYear}
										className={`text-xs py-1 px-2 rounded ${
											isCurrentYear
												? 'bg-blue-100 text-blue-600'
												: isFutureYear
												? 'bg-gray-50 text-gray-300 cursor-not-allowed'
												: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
										}`}
										onClick={() => {
											const newDate = new Date(
												year,
												currentMonth,
												1
											);
											handleMonthSelect(newDate);
										}}
									>
										{year}
									</button>
								);
							})}
						</div>
					</div>
				</div>
			);
		} else if (activeTimeRangeDropdown === 'quarter') {
			// Week wise view (labeled as quarter) shows quarter selector
			return (
				<div
					className='absolute top-full left-0 mt-1 z-50 bg-white shadow-lg rounded-md p-2 border border-gray-200'
					ref={pickerRef}
				>
					<div className='flex space-x-2'>
						<div>
							<p className='text-xs text-gray-500 mb-1'>
								Quarter
							</p>
							<div className='grid grid-cols-2 gap-1'>
								{quarters.map((quarter) => (
									<button
										key={quarter.value}
										className={`text-xs py-1 px-2 rounded ${
											currentQuarter === quarter.value
												? 'bg-blue-100 text-blue-600'
												: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
										}`}
										onClick={() =>
											handleQuarterSelect(quarter.value)
										}
									>
										{quarter.label}
									</button>
								))}
							</div>
						</div>
						<div>
							<p className='text-xs text-gray-500 mb-1'>Year</p>
							<div className='grid grid-rows-4 gap-1'>
								{getYearOptions().map((year) => (
									<button
										key={year}
										className={`text-xs py-1 px-2 rounded ${
											currentYear === year
												? 'bg-blue-100 text-blue-600'
												: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
										}`}
										onClick={() => handleYearSelect(year)}
									>
										{year}
									</button>
								))}
							</div>
						</div>
					</div>
				</div>
			);
		} else if (activeTimeRangeDropdown === 'year') {
			// Month wise view shows year selector
			return (
				<div
					className='absolute top-full left-0 mt-1 z-50 bg-white shadow-lg rounded-md p-2 border border-gray-200'
					ref={pickerRef}
				>
					<p className='text-xs text-gray-500 mb-1'>Year</p>
					<div className='grid grid-cols-2 gap-1'>
						{getYearOptions().map((year) => (
							<button
								key={year}
								className={`text-xs py-1 px-2 rounded ${
									currentYear === year
										? 'bg-blue-100 text-blue-600'
										: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
								}`}
								onClick={() => handleYearSelect(year)}
							>
								{year}
							</button>
						))}
					</div>
				</div>
			);
		}

		return null;
	};

	// Handle outside clicks to close the picker
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				pickerRef.current &&
				!pickerRef.current.contains(event.target)
			) {
				setIsPickerOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () =>
			document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Generate array of recent years
	

	const getDateRangeDisplay = () => {
		if (activeTimeRangeDropdown === 'month') {
			return dateInfo.year && dateInfo.month
				? `${monthNames[dateInfo.month - 1]} - ${dateInfo.year}`
				: 'Select dates';
		} else if (activeTimeRangeDropdown === 'quarter') {
			return dateInfo.year && dateInfo.quarter
				? `${quarters[dateInfo.quarter - 1].label} - ${dateInfo.year}`
				: 'Select dates';
		} else if (activeTimeRangeDropdown === 'year') {
			return dateInfo.year ? `Year ${dateInfo.year}` : 'Select dates';
		}
	};

	// For custom product/geography view, use rsuite DateRangePicker
	if (activeConfigType !== 'time') {
		return (
			<div className='relative w-52 items-center justify-center'>
				<DateRangePicker
					appearance='subtle'
					placeholder='Select Date Range'
					format='dd MMM yyyy'
					style={{
						fontSize: '12px',
						color: '#4a5568',
						fontWeight: '500',
					}}
					value={[dateRangeObj.from, dateRangeObj.to]}
					onChange={handleCustomDateChange}
					character=' to '
					size='sm'
					cleanable={false}
					placement='bottomEnd'
					ranges={quickPickerRanges}
					renderValue={(value) => {
						if (!value || !value[0] || !value[1]) return 'Select Date Range';
						return `${formatDate(value[0])} to ${formatDate(value[1])}`;
					}}
				/>
			</div>
		);
	}

	// For time-based views, use custom date picker
	return (
		<div className='relative w-52 items-center justify-center'>
			<div className={`flex py-1 rounded-lg ${yearChangeHighlight ? 'border-2 border-yellow-600 transition-colors duration-500 ease-in-out' : 'bg-white transition-colors duration-300'} items-center justify-between shadow-sm hover:shadow`}>
				<button
					className='p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200'
					onClick={() => handleNavigate('prev')}
				>
					<ChevronLeft size={16} className="transform transition-transform hover:scale-110" />
				</button>

				<div
					className='flex items-center text-xs text-gray-800 font-medium cursor-pointer mx-.5 hover:text-gray-900 transition-colors duration-200'
					onClick={() => setIsPickerOpen(!isPickerOpen)}
				>
					<span className='text-nowrap'>{getDateRangeDisplay()}</span>
				</div>

				<button
					className='p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200'
					onClick={() => handleNavigate('next')}
				>
					<ChevronRight size={16} className="transform transition-transform hover:scale-110" />
				</button>
			</div>
			{renderTimePicker()}
		</div>
	);
};

export default DateRangeSelector;



const quickPickerRanges=[
	{
		label: 'Last 7 days',
		value: [
			new Date(new Date().setDate(new Date().getDate() - 7)),
			new Date()
		]
	},
	{
		label: 'Last 30 days',
		value: [
			new Date(new Date().setDate(new Date().getDate() - 30)),
			new Date()
		]
	},
	{
		label: 'This month',
		value: [
			new Date(new Date().setDate(1)),
			new Date()
		]
	},
	{
		label: 'Last month',
		value: [
			new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
			new Date(new Date().getFullYear(), new Date().getMonth(), 0)
		]
	},
	{
		label: 'Last 3 months',
		value: [
			new Date(new Date().setMonth(new Date().getMonth() - 3)),
			new Date()
		]
	},
	{
		label: 'Last 6 months',
		value: [
			new Date(new Date().setMonth(new Date().getMonth() - 6)),
			new Date()
		]
	},
	{
		label: 'This year',
		value: [
			new Date(new Date().getFullYear(), 0, 1),
			new Date()
		]
	},
	{
		label: 'Last year',
		value: [
			new Date(new Date().getFullYear() - 1, 0, 1),
			new Date(new Date().getFullYear() - 1, 11, 31)
		]
	}
]